import React, { useState, useEffect } from "react";

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const heroImages = [
    "./hero/aero.jpg",
    "./hero/bionic_lab.png",
    "./hero/machine_shop.jpg",
  ];

  useEffect(() => {
    // Only auto rotate images if autoplay is enabled
    if (!autoplayEnabled) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 8000); // Changed from 5000ms to 8000ms (8 seconds between transitions)

    return () => clearInterval(interval);
  }, [autoplayEnabled, heroImages.length]);

  // Pause autoplay for a few seconds when manually changing slides
  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
    setAutoplayEnabled(false);
    // Resume autoplay after 10 seconds
    setTimeout(() => setAutoplayEnabled(true), 10000);
  };

  return (
    <section className="relative w-full min-h-[110vh] sm:min-h-[100vh] md:min-h-[110vh] text-white overflow-hidden">
      {/* Full-width Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity ${
              index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDuration: "2000ms" }}
          >
            <img
              src={img}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/30"></div>
      </div>

      {/* Enhanced Decorative Background Elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-8 sm:top-16 left-4 sm:left-8 w-16 h-16 sm:w-32 sm:h-32 bg-accent-500/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-8 sm:bottom-16 right-4 sm:right-8 w-20 h-20 sm:w-40 sm:h-40 bg-secondary-500/10 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/3 w-12 h-12 sm:w-24 sm:h-24 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute top-1/4 right-1/4 w-10 h-10 sm:w-20 sm:h-20 bg-info-500/8 rounded-full blur-lg"></div>
      </div>

      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-3 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='40' cy='40' r='2'/%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3Ccircle cx='60' cy='60' r='1.5'/%3E%3Ccircle cx='20' cy='60' r='1'/%3E%3Ccircle cx='60' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      {/* Content over Image */}
      <div className="relative z-10 w-full h-full min-h-[110vh] sm:min-h-[100vh] md:min-h-[110vh] flex flex-col">
        {/* Centered Layout for all screen sizes */}
        <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
          {/* Main Title - Centered on all screens */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white animate-fade-in mb-4 sm:mb-6">
              <span className="block">Welcome to the</span>
              <span className="block">University of Moratuwa</span>
              <span className="block">Mechanical Engineering Society</span>
              {/* <span className="block">
                (UMMES)
              </span> */}
            </h1>
          </div>

          {/* Department Info - Centered on all screens */}
          <div className="max-w-2xl mx-auto">
            {/* Description */}
            <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed mb-6 text-center">
              UMMES is a collaborative forum of the Department of Mechanical
              Engineering, uniting students, academics, alumni, and
              professionals to foster engagement, learning, and meaningful
              connections within the department. We work to enhance the academic
              and professional environment, support initiatives that strengthen
              the department, and promote collaboration across all levels of the
              Mechanical Engineering community.
            </p>
          </div>
          <div className="flex justify-center items-center">
            <a
              href="https://uom.lk/mech"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <button className="relative px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-full hover:from-amber-700 hover:to-amber-600 transition-all duration-300 ease-in-out transform group-hover:scale-105 group-hover:shadow-lg border border-transparent group-hover:border-white/20 overflow-hidden">
                <span className="absolute inset-0 w-full h-full bg-white/30 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>

                <span className="relative flex items-center justify-center space-x-3">
                  <span>Department Website</span>
                </span>
              </button>
            </a>
          </div>
        </div>
      </div>

      {/* Image Carousel Indicator Dots */}
      <div className="absolute bottom-32 sm:bottom-36 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center justify-center space-x-2 sm:space-x-3 bg-black/20 backdrop-blur-sm rounded-full px-3 py-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index === currentImageIndex
                  ? "w-8 sm:w-10 bg-white shadow-md shadow-white/30"
                  : "w-2 sm:w-2.5 bg-white/60 hover:bg-white/80 hover:w-4"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-12 sm:bottom-16 left-1/2 transform -translate-x-1/2 z-20 animate-bounce"
        style={{ marginLeft: "-10mm" }}
      >
        <div className="flex flex-col items-center justify-center space-y-1 sm:space-y-2">
          <span className="text-primary-200 text-xs font-medium bg-white/10 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
            Scroll down
          </span>
          <div className="w-5 h-6 sm:w-6 sm:h-8 border-2 border-primary-200/50 rounded-full flex justify-center items-center">
            <div className="w-0.5 h-1.5 sm:h-2 bg-primary-200 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
